import React from 'react';
import { useSchool } from '../context/SchoolContext';
import { Users, MapPin, Clock } from 'lucide-react';

const Leads = () => {
    const { leads, loading, error, selectedBranch, loadMoreLeads, hasMoreLeads, totalLeadsCount } = useSchool();

    if (loading) return <div className="p-8">Loading leads...</div>;
    if (error) return <div className="p-8 text-rose-500">{error}</div>;

    return (
        <div className="animate-in">
            <div className="flex justify-between items-center mb-8">
                <div>
                    <h1 className="text-4xl font-black text-slate-900 tracking-tight">Lead Analytics</h1>
                    <p className="text-slate-500 mt-1">Viewing all leads for {selectedBranch}</p>
                </div>
            </div>

            <div className="bg-white rounded-[2rem] border border-slate-100 shadow-xl overflow-hidden">
                <div className="p-8 border-b border-slate-50 flex justify-between items-center bg-slate-50/50">
                    <h3 className="font-bold text-slate-800 text-lg">Active Leads Pipeline</h3>
                    <div className="flex gap-2">
                        <span className="bg-blue-50 text-blue-600 px-4 py-1.5 rounded-full text-xs font-bold uppercase tracking-widest">
                            {leads.length} Total Leads
                        </span>
                    </div>
                </div>

                <div className="overflow-x-auto">
                    <table className="w-full">
                        <thead>
                            <tr className="bg-slate-50/50">
                                <th className="px-8 py-4 text-left text-[11px] font-black uppercase tracking-widest text-slate-400 border-b border-slate-100">Contact / Source</th>
                                <th className="px-8 py-4 text-left text-[11px] font-black uppercase tracking-widest text-slate-400 border-b border-slate-100">Branch</th>
                                <th className="px-8 py-4 text-left text-[11px] font-black uppercase tracking-widest text-slate-400 border-b border-slate-100">Status</th>
                                <th className="px-8 py-4 text-left text-[11px] font-black uppercase tracking-widest text-slate-400 border-b border-slate-100">Last Updated</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y">
                            {leads.length === 0 ? (
                                <tr>
                                    <td colSpan="4" className="px-8 py-12 text-center text-slate-400 italic">No leads found for this selection.</td>
                                </tr>
                            ) : (
                                leads.map((lead) => (
                                    <tr key={lead.id} className="hover:bg-slate-50/80 transition-all duration-200">
                                        <td className="px-8 py-6">
                                            <div className="flex items-center gap-4">
                                                <div className="w-12 h-12 rounded-2xl bg-indigo-50 flex items-center justify-center text-indigo-600 shadow-sm border border-indigo-100">
                                                    <Users size={22} />
                                                </div>
                                                <div>
                                                    <div className="font-bold text-slate-900 text-lg leading-tight">
                                                        {lead.first_name || 'Unknown'} {lead.last_name || 'Lead'}
                                                    </div>
                                                    <div className="text-slate-400 text-sm mt-1 flex items-center gap-2">
                                                        <span className="bg-emerald-50 text-emerald-600 px-2 py-0.5 rounded text-[10px] font-black uppercase tracking-tighter">
                                                            {lead.lead_source || 'Website'}
                                                        </span>
                                                        <span>{lead.phone_number || 'No phone'}</span>
                                                    </div>
                                                </div>
                                            </div>
                                        </td>
                                        <td className="px-8 py-6">
                                            <div className="flex items-center gap-2 text-slate-700 font-medium">
                                                <MapPin size={16} className="text-slate-400" />
                                                {lead.branch || 'Not assigned'}
                                            </div>
                                        </td>
                                        <td className="px-8 py-6">
                                            <span className={`
                                                px-3 py-1 rounded-lg text-xs font-bold uppercase tracking-widest
                                                ${lead.status?.toLowerCase() === 'new' ? 'bg-blue-50 text-blue-600 border border-blue-100' : 'bg-slate-100 text-slate-600'}
                                            `}>
                                                {lead.status || 'New'}
                                            </span>
                                        </td>
                                        <td className="px-8 py-6">
                                            <div className="flex items-center gap-2 text-slate-500 text-sm">
                                                <Clock size={16} />
                                                {lead.updated_at || lead.created_at ? new Date(lead.updated_at || lead.created_at).toLocaleString() : 'Just now'}
                                            </div>
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                    {hasMoreLeads && (
                        <div className="flex justify-center p-6 border-t border-slate-100">
                            <button className="bg-slate-100 text-slate-600 px-6 py-2 rounded-full font-bold text-sm tracking-widest uppercase hover:bg-slate-200 transition-colors" onClick={loadMoreLeads}>
                                Load More ({leads.length} of {totalLeadsCount})
                            </button>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default Leads;
